import React, { useState, useEffect, useCallback } from "react";
import { FiCpu, FiLoader, FiChevronDown, FiChevronUp, FiLogIn, FiZap } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { PAPER_SKILLS } from "../lib/paperSkills";
import "./AiEditPanel.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

export default function AiEditPanel({
  code,
  template,
  onApply,
  disabled,
  texts,
  placement = "preview",
  paperSkill = "none",
  onPaperSkillChange,
}) {
  const [open, setOpen] = useState(placement !== "preview");
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quota, setQuota] = useState(null);
  const { canUseAi, isLoggedIn, session, openLoginModal, openPlanModal, openUsageModal, devUnlocked } = useAuth();
  const t = texts?.ai || {};
  const tb = texts?.billing || {};

  const fetchQuota = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${API_BASE}/api/ai/quota`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data && typeof data.remaining === "number") {
        setQuota(data);
      }
    } catch {
      /* ignore */
    }
  }, [session?.access_token]);

  useEffect(() => {
    if (canUseAi && isLoggedIn && !devUnlocked) {
      fetchQuota();
    }
  }, [canUseAi, isLoggedIn, devUnlocked, fetchQuota]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canUseAi || loading || disabled) return;
    const trimmed = instruction.trim();
    if (!trimmed) {
      setError(t.instructionRequired || "Enter an instruction");
      return;
    }
    if (!code || !code.trim()) {
      setError(t.codeRequired || "Paste or write code first");
      return;
    }
    if (quota && quota.remaining <= 0) {
      setError(t.quotaExhausted || "Monthly AI edit quota exceeded");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }
      const res = await fetch(`${API_BASE}/api/ai/edit`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          code,
          instruction: trimmed,
          template,
          paperSkill: paperSkill === "none" ? null : paperSkill,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.quota) setQuota(data.quota);
        throw new Error(data.error || t.requestFailed || "AI edit failed");
      }
      if (typeof data.code !== "string") {
        throw new Error(t.invalidResponse || "Invalid AI response");
      }
      if (data.quota) setQuota(data.quota);
      onApply(data.code);
      setInstruction("");
    } catch (err) {
      setError(err.message || t.requestFailed || "AI edit failed");
    } finally {
      setLoading(false);
    }
  };

  const rootClass = [
    "ai-edit-panel",
    placement === "preview" ? "ai-edit-panel--preview" : "",
    loading ? "ai-edit-panel--loading" : "",
    !canUseAi ? "ai-edit-panel--locked" : "",
    template === "markdown" ? "ai-edit-panel--unsupported" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!canUseAi) {
    return (
      <div className={rootClass}>
        <div className="ai-edit-panel__locked-row">
          <FiCpu />
          <span>{t.title || "AI Edit"}</span>
          <button
            type="button"
            className="ai-edit-panel__login"
            onClick={openLoginModal}
          >
            <FiLogIn /> {t.loginRequired || "ログインして利用"}
          </button>
        </div>
        <p className="ai-edit-panel__locked-note">
          {t.loginNote || "ログインしてAI修正を利用できます。"}
        </p>
      </div>
    );
  }

  // Markdown is out of scope for AI edit: the planner + Morph Fast Apply
  // pipeline is code-oriented and degrades on prose documents. Show a short
  // notice instead of the edit form. The server also rejects markdown as
  // insurance (handleAiEdit returns 400).
  if (template === "markdown") {
    return (
      <div className={rootClass}>
        <div className="ai-edit-panel__locked-row">
          <FiCpu />
          <span>{t.title || "AI Edit"}</span>
        </div>
        <p className="ai-edit-panel__locked-note">
          {t.markdownNotSupported ||
            "AI Edit supports HTML / React / Canvas. For Markdown, edit directly in the editor."}
        </p>
      </div>
    );
  }

  const exhausted = Boolean(quota && quota.remaining <= 0);

  return (
    <div className={rootClass}>
      <button
        type="button"
        className="ai-edit-panel__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ai-edit-panel__toggle-label">
          <FiCpu />
          {t.title || "AI Edit"}
        </span>
        {open ? <FiChevronUp /> : <FiChevronDown />}
      </button>

      {open && (
        <form className="ai-edit-panel__body" onSubmit={handleSubmit}>
          {template !== "markdown" ? (
            <div className="ai-edit-panel__skill-row">
              <label className="ai-edit-panel__label" htmlFor="ai-edit-paper-skill">
                {t.paperSkillLabel || "Paper skill"}
              </label>
              <select
                id="ai-edit-paper-skill"
                className="ai-edit-panel__skill-select"
                value={paperSkill}
                onChange={(e) => onPaperSkillChange?.(e.target.value)}
                disabled={loading || disabled || exhausted || !onPaperSkillChange}
                title={t.paperSkillHint || ""}
              >
                <option value="none">{t.paperSkillNone || "None"}</option>
                {PAPER_SKILLS.map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    {t.paperSkills?.[skill.id] || skill.cssPage}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <label className="ai-edit-panel__label" htmlFor="ai-edit-instruction">
            {t.instructionLabel || "What should change?"}
          </label>
          <div className="ai-edit-panel__row">
            <textarea
              id="ai-edit-instruction"
              className="ai-edit-panel__input"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder={t.placeholder || "e.g. Make the button green and add a reset control"}
              rows={2}
              disabled={loading || disabled || exhausted}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  if (!loading && instruction.trim() && code?.trim() && !exhausted) {
                    e.currentTarget.form?.requestSubmit();
                  }
                }
              }}
            />
            <button
              type="submit"
              className="ai-edit-panel__submit"
              disabled={loading || disabled || !instruction.trim() || !code?.trim() || exhausted}
            >
              {loading ? (
                <>
                  <FiLoader className="ai-edit-panel__spin" />
                  {t.applying || "Applying…"}
                </>
              ) : (
                <>
                  <FiCpu />
                  {t.apply || "Apply with AI"}
                </>
              )}
            </button>
          </div>
          {error ? <p className="ai-edit-panel__error" role="alert">{error}</p> : null}
          {quota && !devUnlocked ? (
            <>
              <p
                className={`ai-edit-panel__quota ${exhausted ? "ai-edit-panel__quota--out" : ""}`}
                role="button"
                tabIndex={0}
                onClick={openUsageModal}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openUsageModal();
                  }
                }}
                title={t.viewUsage || "利用状況を見る"}
                style={{ cursor: "pointer" }}
              >
                {exhausted
                  ? (t.quotaExhausted || "今月の利用枠に達しました")
                  : (t.quotaPercent || "消化率 {pct}%")
                      .replace(
                        "{pct}",
                        String(
                          Math.min(
                            100,
                            Math.round(
                              (Number(quota.used) / Math.max(Number(quota.quota), 1)) * 100
                            )
                          )
                        )
                      )}
                {exhausted ? (
                  <button
                    type="button"
                    className="ai-edit-panel__upgrade"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPlanModal();
                    }}
                  >
                    <FiZap /> {tb.upgrade || t.upgradeNote || "アップグレード"}
                  </button>
                ) : null}
              </p>
            </>
          ) : (
            <p className="ai-edit-panel__hint">
              {t.hint || "Partial edits via planner + Morph Apply. Undo restores the previous code."}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
